/**
 * Browser-side evidence for bootstrap failures, attached to the /monitoring/client-error report.
 *
 * Chrome's "Failed to fetch dynamically imported module: <url>" names the root of the import
 * (the bootstrap chunk) even when a nested static dependency is what actually failed, and it
 * never says why. Frontdoor logs show the chunk being served fine (or never requested at all)
 * right before these failures, so the cause is inside the browser - a proxy/AV rewriting or
 * blocking the response, an extension, a CSP block, or a bad HTTP cache entry. This captures
 * enough to tell those apart without needing the user's console.
 */

import { ModuleProbeResult, probeModuleMap, SpecifierResolver } from './module-map-probe.util';

const MAX_CSP_VIOLATIONS = 5;
const MAX_SUSPECT_RESOURCES = 10;
const PROBE_TIMEOUT_MS = 5_000;
// The server stores this as a single App Insights custom dimension, which is capped at 8192 chars
const MAX_DIAGNOSTICS_LENGTH = 7_500;
// Per free-text field (URLs, CSP directives, probe errors) once the payload needs trimming
const MAX_FIELD_LENGTH = 300;

interface CspViolation {
    blockedURI: string;
    directive: string;
    disposition: string;
}

interface ResourceSummary {
    path: string;
    status: number | null;
    transferSize: number;
    decodedBodySize: number;
    durationMs: number;
}

interface ProbeResult {
    status?: number;
    contentType?: string | null;
    bytes?: number;
    error?: string;
    durationMs: number;
}

export interface BootstrapDiagnostics {
    msSinceNavigationStart: number;
    navigationType: string | null;
    online: boolean;
    visibility: DocumentVisibilityState;
    effectiveConnectionType: string | null;
    failedUrl: string | null;
    failedUrlTiming: ResourceSummary | null;
    scriptResourceCount: number;
    suspectResources: ResourceSummary[];
    cspViolations: CspViolation[];
    // fetch() of failedUrl from the HTTP cache only - an error here with a 200 network probe
    // points at a bad cache entry, which a plain reload can't fix since chunks are `immutable`
    cacheProbe: ProbeResult | null;
    // fetch() of failedUrl bypassing the cache - a failure here while Frontdoor logged a clean
    // response means something between the browser and Frontdoor is interfering
    networkProbe: ProbeResult | null;
    // Which module in the bootstrap graph the browser actually gave up on - see module-map-probe.util.ts
    moduleProbe: ModuleProbeResult | null;
}

const cspViolations: CspViolation[] = [];

/**
 * Call as early as possible in main-mfe.ts - CSP violations that happen before the listener is
 * attached are lost.
 */
export function startBootstrapDiagnostics(): void {
    // The default of 250 entries is less than the bootstrap graph the module probe walks, so a
    // missing timing entry could otherwise mean either "never fetched" or "buffer full"
    performance.setResourceTimingBufferSize(1000);
    document.addEventListener('securitypolicyviolation', (ev: SecurityPolicyViolationEvent) => {
        if (cspViolations.length < MAX_CSP_VIOLATIONS) {
            cspViolations.push({ blockedURI: ev.blockedURI, directive: ev.effectiveDirective, disposition: ev.disposition });
        }
    });
}

/**
 * `resolveBare` resolves a bare specifier through the page's import map - pass
 * `(specifier) => import.meta.resolve(specifier)` from the entry module. It's injected rather than
 * called here so this file stays free of import.meta, which the CommonJS test build can't parse.
 */
export async function collectBootstrapDiagnostics(err: unknown, resolveBare?: SpecifierResolver): Promise<BootstrapDiagnostics> {
    const message = err instanceof Error ? err.message : String(err);
    const failedUrl = message.match(/https?:\/\/\S+?\.m?js\b/)?.[0] ?? null;

    // Same-origin only: cross-origin entries (pendo, chargebee) report zero sizes without
    // Timing-Allow-Origin, which would look identical to a failed fetch below
    const scripts = (performance.getEntriesByType('resource') as PerformanceResourceTiming[]).filter(
        (entry) => entry.name.startsWith(window.location.origin) && /\.(m?js|json)(\?|$)/.test(entry.name)
    );
    const failedEntry = failedUrl ? scripts.find((entry) => entry.name === failedUrl) : undefined;

    const [cacheProbe, networkProbe, moduleProbe] = failedUrl
        ? await Promise.all([
              // only-if-cached is only allowed with same-origin mode; failedUrl is always same-origin here
              probe(failedUrl, { cache: 'only-if-cached', mode: 'same-origin' }),
              probe(failedUrl, { cache: 'no-store' }),
              // Graph walk rejects nothing - it records its own errors in the result
              resolveBare ? probeModuleMap(failedUrl, resolveBare) : Promise.resolve(null),
          ])
        : [null, null, null];

    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;

    return {
        msSinceNavigationStart: Math.round(performance.now()),
        navigationType: navigation?.type ?? null,
        online: navigator.onLine,
        visibility: document.visibilityState,
        effectiveConnectionType: (navigator as { connection?: { effectiveType?: string } }).connection?.effectiveType ?? null,
        failedUrl,
        failedUrlTiming: failedEntry ? summarise(failedEntry) : null,
        scriptResourceCount: scripts.length,
        // A failed or blocked fetch shows up with no body (and responseStatus 0 in Chromium); a
        // cache hit has a body but transferSize 0, so decodedBodySize is the signal rather than
        // transferSize. responseStatus is undefined outside Chromium, so only trust a real code.
        suspectResources: scripts
            .filter(
                (entry) =>
                    entry.decodedBodySize === 0 || (entry.responseStatus !== undefined && ![0, 200, 304].includes(entry.responseStatus))
            )
            .slice(0, MAX_SUSPECT_RESOURCES)
            .map(summarise),
        cspViolations,
        cacheProbe,
        networkProbe,
        moduleProbe,
    };
}

/**
 * JSON for the report body, trimmed to fit one App Insights custom dimension. Never slices the
 * JSON string itself - that would leave invalid JSON that parse_json can't read. Drops the
 * per-resource detail first since the probes and counts are the more useful signal, then
 * clips the free-text fields, then falls back to a compact summary that always fits.
 */
export function serialiseBootstrapDiagnostics(diagnostics: BootstrapDiagnostics): string {
    const reduced = {
        ...diagnostics,
        suspectResources: diagnostics.suspectResources.slice(0, 3),
        moduleProbe: diagnostics.moduleProbe && { ...diagnostics.moduleProbe, failed: diagnostics.moduleProbe.failed.slice(0, 3) },
    };
    const bounded = {
        ...reduced,
        failedUrl: diagnostics.failedUrl && clip(diagnostics.failedUrl),
        failedUrlTiming: diagnostics.failedUrlTiming && clipResource(diagnostics.failedUrlTiming),
        suspectResources: reduced.suspectResources.map(clipResource),
        cspViolations: diagnostics.cspViolations.map((violation) => ({
            blockedURI: clip(violation.blockedURI),
            directive: clip(violation.directive),
            disposition: clip(violation.disposition),
        })),
        cacheProbe: diagnostics.cacheProbe && clipProbe(diagnostics.cacheProbe),
        networkProbe: diagnostics.networkProbe && clipProbe(diagnostics.networkProbe),
        moduleProbe: reduced.moduleProbe && {
            ...reduced.moduleProbe,
            failed: reduced.moduleProbe.failed.map((failed) => ({
                ...failed,
                path: clip(failed.path),
                specifier: clip(failed.specifier),
                parentPath: failed.parentPath && clip(failed.parentPath),
            })),
            unresolved: reduced.moduleProbe.unresolved.map(clip),
            error: reduced.moduleProbe.error && clip(reduced.moduleProbe.error),
        },
    };

    for (const candidate of [diagnostics, reduced, bounded]) {
        const json = JSON.stringify(candidate);
        if (json.length <= MAX_DIAGNOSTICS_LENGTH) {
            return json;
        }
    }

    // Numbers, booleans, short enum strings and one clipped path, so this is always well under the limit
    return JSON.stringify({
        truncated: true,
        msSinceNavigationStart: diagnostics.msSinceNavigationStart,
        navigationType: diagnostics.navigationType,
        online: diagnostics.online,
        visibility: diagnostics.visibility,
        scriptResourceCount: diagnostics.scriptResourceCount,
        suspectResourceCount: diagnostics.suspectResources.length,
        cspViolationCount: diagnostics.cspViolations.length,
        cacheProbeStatus: diagnostics.cacheProbe?.status ?? null,
        networkProbeStatus: diagnostics.networkProbe?.status ?? null,
        moduleProbeFailedCount: diagnostics.moduleProbe?.failedCount ?? null,
        moduleProbeFirstFailedPath: diagnostics.moduleProbe?.failed[0] ? clip(diagnostics.moduleProbe.failed[0].path) : null,
    });
}

function clip(value: string): string {
    return value.length > MAX_FIELD_LENGTH ? `${value.slice(0, MAX_FIELD_LENGTH)}…` : value;
}

function clipResource(resource: ResourceSummary): ResourceSummary {
    return { ...resource, path: clip(resource.path) };
}

function clipProbe(probeResult: ProbeResult): ProbeResult {
    return {
        ...probeResult,
        contentType: probeResult.contentType && clip(probeResult.contentType),
        error: probeResult.error && clip(probeResult.error),
    };
}

function summarise(entry: PerformanceResourceTiming): ResourceSummary {
    return {
        path: new URL(entry.name).pathname,
        // responseStatus is Chromium-only (109+); Safari and Firefox leave it undefined
        status: entry.responseStatus ?? null,
        transferSize: entry.transferSize,
        decodedBodySize: entry.decodedBodySize,
        durationMs: Math.round(entry.duration),
    };
}

async function probe(url: string, init: RequestInit): Promise<ProbeResult> {
    const startedAt = performance.now();
    try {
        const response = await fetch(url, { ...init, signal: AbortSignal.timeout(PROBE_TIMEOUT_MS) });
        const body = await response.text();
        return {
            status: response.status,
            contentType: response.headers.get('content-type'),
            bytes: body.length,
            durationMs: Math.round(performance.now() - startedAt),
        };
    } catch (error) {
        return {
            error: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
            durationMs: Math.round(performance.now() - startedAt),
        };
    }
}
